<template>
    <div class="app-search">
        <div class="app-search-field-wrapper" :class="{ 'has-value': value }">
            <!-- Label -->
            <label
                v-if="label && !hideDetails"
                class="app-search-label"
                :class="{ 'has-value': value || display, 'is-focused': showMenu, 'has-error': showError }">
                {{ label }}
            </label>

            <!-- Main Input Field -->
            <div
                class="app-search-input-container"
                :class="{ 'has-label': label && !hideDetails, 'is-focused': showMenu, 'has-error': showError }"
                @click="openMenu">
                <input
                    ref="displayField"
                    class="app-search-input"
                    :value="display"
                    readonly
                    autocomplete="off"
                    type="text">
                <button
                    v-if="value"
                    type="button"
                    class="app-search-clear-btn"
                    @click.stop="remove">
                    <v-icon>mdi-close</v-icon>
                </button>
                <v-icon class="app-search-dropdown-icon">
                    mdi-menu-down
                </v-icon>
            </div>

            <!-- Error Messages / Rules -->
            <div v-if="!hideDetails" class="app-search-details">
                <span v-if="showError" class="app-search-error">
                    {{ validationError }}
                </span>
            </div>
        </div>

        <!-- Dropdown Menu -->
        <div
            v-if="showMenu"
            ref="menuContainer"
            class="app-search-menu"
            @click.stop>
            <!-- Search Field in Menu -->
            <div class="app-search-menu-search">
                <input
                    ref="searchField"
                    v-model="searchText"
                    class="app-search-menu-input"
                    type="text"
                    placeholder="Search..."
                    autocomplete="off"
                    @keydown="onKeyDown">
            </div>

            <!-- Loading -->
            <div v-if="loading" class="app-search-loading">
                <div class="app-search-spinner" />
                <span>Searching...</span>
            </div>

            <!-- Results List -->
            <div v-else-if="items.length" class="app-search-results">
                <div
                    v-for="(item, index) in items"
                    :key="item.id"
                    class="app-search-result-item"
                    :class="{ 'is-selected': index === selectedIndex }"
                    @click="onItemClick(item)">
                    {{ item.label }}
                </div>
            </div>
            <div v-else-if="results" class="app-search-no-results">
                No results found
            </div>
        </div>

        <!-- Backdrop to close menu -->
        <div
            v-if="showMenu"
            class="app-search-backdrop"
            @click="closeMenu" />
    </div>
</template>

<script>
import { isNullOrWhiteSpace, trim } from "@/services/stringUtility";
import { buildSelectQuery } from "@/features/schemas/services/searcher"
import { getTableData } from "@/features/schemas/services/tableService";
import { getEntity } from "@/features/schemas/services/schemaProvider";
import { get } from "@/features/schemas/services/schemaApi";
import { getLabel } from "@/features/schemas/services/labeller";
import { debounce } from "@/services/debounce";
import { or, and, equal } from "@/services/filtering";

export default {
    props: {
        entityKey: {
            type: String,
            default: null,
            required: true
        },
        value: {
            type: Number,
            default: null
        },
        rules: {
            type: Array,
            default: () => []
        },
        label: {
            type: String,
            default: () => "Search"
        },
        filter: {
            type: Object,
            default: null,
        },
        searchOptions: {
            type: Object,
            default: null,
        },
        searchWhenBlank: {
            type: Boolean,
            default: true
        },
        hideDetails: {
            type: Boolean,
            default: false
        },
        scope: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            searchText: null,
            results: null,
            selectedIndex: -1,
            selectedItem: null,
            showMenu: false,
            dirty: false,
            loading: false,
        }
    },
    computed: {
        entityHasIsDisabled() {
            const entity = getEntity(this.entityKey);
            return 'isDisabled' in (entity.commandFields ?? {});
        },
        items() {
            return this.results?.items ?? [];
        },
        display() {
            if (this.selectedItem == null) {
                return "";
            }

            return this.selectedItem.label;
        },
        searchIsBlank() {
            return isNullOrWhiteSpace(trim(this.searchText, "\""));
        },
        validationError() {
            if (!this.rules || this.rules.length === 0) {
                return null;
            }
            for (let rule of this.rules) {
                const result = rule(this.value);
                if (result !== true) {
                    return result;
                }
            }
            return null;
        },
        showError() {
            return this.dirty && this.validationError;
        },
    },
    watch: {
        searchText: debounce(function () {
            return this.search();
        }, 250),
        selectedItem(selectedItem) {
            if(this.value !== selectedItem?.id) {
                this.$emit("input", selectedItem?.id);
                this.$emit("item", selectedItem)
            }
        },
        value: {
            immediate: true,
            async handler(value) {
                if (!value) {
                    this.selectedItem = null;
                    return;
                }

                if (this.selectedItem?.id !== value) {
                    let item = await get(this.entityKey, this.scope, value);
                    item.label = getLabel(this.entityKey, item);
                    this.selectedItem = item;
                }
            }
        }
    },
    mounted() {
        if (this.showMenu) {
            this.positionMenu();
        }
    },
    methods: {
        openMenu() {
            if (!this.showMenu) {
                this.showMenu = true;
                this.search();
                this.$nextTick(() => {
                    this.positionMenu();
                    if (this.$refs.searchField) {
                        this.$refs.searchField.focus();
                    }
                });
            }
        },
        closeMenu() {
            this.showMenu = false;
            this.searchText = null;
            this.results = null;
            this.selectedIndex = -1;
            this.dirty = true;
            this.loading = false;
        },
        positionMenu() {
            if (!this.$refs.menuContainer) return;

            const inputContainer = this.$el.querySelector('.app-search-input-container');
            if (!inputContainer) return;

            const rect = inputContainer.getBoundingClientRect();
            const menu = this.$refs.menuContainer;

            menu.style.top = `${rect.bottom + 1}px`;
            menu.style.left = `${rect.left}px`;
            menu.style.width = `${rect.width}px`;
        },
        async search() {
            if (!this.searchWhenBlank && this.searchIsBlank) {
                this.results = null;
                return;
            }

            let searchText = this.searchText;

            this.loading = true;

            let searchFilter = this.filter;

            if (this.entityHasIsDisabled) {
                const disabledFilter = equal("isDisabled", false, "bool");
                searchFilter = searchFilter
                    ? and([searchFilter, disabledFilter])
                    : disabledFilter;
            }

            if (this.value && searchFilter) {
                searchFilter = or([
                    searchFilter,
                    equal("id", this.value)
                ]);
            }

            let options = {
                filter: searchFilter,
                sortBy: this.searchOptions?.sortBy,
                direction: this.searchOptions?.direction,
                take: this.searchOptions?.take,
            };

            let model = buildSelectQuery(this.entityKey, searchText, options);

            let results = await getTableData(this.entityKey, model, this.scope);

            results.items.forEach(item => item.label = getLabel(this.entityKey, item));
            results.headers.push({
                value: "label",
                text: getEntity(this.entityKey).singleTitle
            });

            if (searchText === this.searchText) {
                this.results = results;
                this.loading = false;
            }
        },
        onKeyDown(e) {
            let handledKeys = ["ArrowDown", "ArrowUp", "Enter", "Tab"];

            if (handledKeys.includes(e.key)) {
                let handler = this[`on${e.key}`];
                handler(e);
            }
        },
        onArrowDown(e) {
            let index = this.selectedIndex + 1;
            if (index < this.items.length) {
                this.selectedIndex = index;
            }
            e.preventDefault();
        },
        onArrowUp(e) {
            let index = this.selectedIndex - 1;
            if (index > -1) {
                this.selectedIndex = index;
            }
            e.preventDefault();
        },
        onEnter(e) {
            if (this.selectedIndex > -1) {
                this.selectedItem = this.items[this.selectedIndex];
                this.clear();
            }
            e.preventDefault();
        },
        onTab() {
            if (this.selectedIndex > -1) {
                this.selectedItem = this.items[this.selectedIndex];
                this.clear();
            }
        },
        onItemClick(item) {
            this.selectedItem = item;
            this.clear();
        },
        clear() {
            this.closeMenu();
        },
        remove() {
            this.selectedItem = null;
            this.dirty = true;
        },
    }
}
</script>

<style lang="scss" scoped>
@import "@/styles/theme.scss";

.app-search {
    position: relative;
}

.app-search-field-wrapper {
    position: relative;
    width: 100%;
}

.app-search-label {
    position: absolute;
    left: 12px;
    top: 38%;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    pointer-events: none;
    transform: translateY(-50%);
    transform-origin: left center;
    transition: transform 0.2s cubic-bezier(0.0, 0, 0.2, 1),
    color 0.2s cubic-bezier(0.0, 0, 0.2, 1);
    will-change: transform;
    z-index: 1;
    background: white;
    padding: 0 4px;
    margin-left: -4px;
    line-height: 20px;

    &.has-value,
    &.is-focused {
        transform: translateY(calc(-50% - 20px)) scale(0.75);
    }

    &.is-focused {
        color: $color-primary;
    }

    &.has-error {
        color: #c52e00;
    }
}

.app-search-clear-btn {
    position: absolute;
    right: 28px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.54);
    z-index: 2;
    width: 24px;
    height: 24px;

    &:hover {
        color: rgba(0, 0, 0, 0.87);
    }

    .v-icon {
        font-size: 18px;
    }
}

.app-search-input-container {
    position: relative;
    cursor: pointer;
    border: 1px solid black;
    transition: border-color 0.2s cubic-bezier(0.0, 0, 0.2, 1),
    border-width 0.2s cubic-bezier(0.0, 0, 0.2, 1);

    &:hover {
        border-color: rgba(0, 0, 0, 0.87);
    }

    &.is-focused {
        border-color: $color-primary;
        border-width: 2px;
    }

    &.has-error {
        border-color: #c52e00;

        &.is-focused {
            border-color: #c52e00;
        }
    }
}

.app-search-input {
    width: 100%;
    padding: 10px 56px 10px 10px;
    border: none;
    outline: none;
    font-size: 16px;
    line-height: 20px;
    background: transparent;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.87);
}

.app-search-dropdown-icon {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: rgba(0, 0, 0, 0.54);
    font-size: 24px;
}

.app-search-details {
    min-height: 14px;
    padding: 2px 12px 0;
    margin-bottom: 2px;
}

.app-search-error {
    font-size: 12px;
    color: #c52e00;
    line-height: 12px;
}

.app-search-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99;
}

.app-search-menu {
    position: fixed;
    z-index: 100;
    background-color: white;
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2),
    0px 8px 10px 1px rgba(0, 0, 0, 0.14),
    0px 3px 14px 2px rgba(0, 0, 0, 0.12);
    max-height: 360px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-top: 0;
}

.app-search-menu-search {
    padding: 6px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;
}

.app-search-menu-input {
    width: 100%;
    padding: 5px 12px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    outline: none;
    font-size: 14px;
    transition: border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    &:focus {
        border-color: $color-primary;
    }

    &::placeholder {
        color: rgba(0, 0, 0, 0.38);
    }
}

.app-search-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
}

.app-search-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(0, 0, 0, 0.12);
    border-top-color: $color-primary;
    border-radius: 50%;
    animation: app-search-spin 0.6s linear infinite;
    flex-shrink: 0;
}

@keyframes app-search-spin {
    to {
        transform: rotate(360deg);
    }
}

.app-search-results {
    overflow-y: auto;
    max-height: 304px;
}

.app-search-result-item {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }

    &.is-selected {
        color: $color-primary;
        background-color: rgba($color-primary, 0.12);
    }
}

.app-search-no-results {
    padding: 16px;
    text-align: center;
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
}
</style>
